import { ApolloClient } from "@apollo/client";
import type {
  IUpdateUserProps,
  UpdateUserBrandDTO,
  UpdateUserDTO,
} from "@src/domain/dto/users";
import type { UserDetailsBrand } from "@src/domain/entities/auth/user/UserDetails";
import type { IUserRepository } from "@src/domain/repositories/user";
import { UPDATE_USER_BRAND } from "@src/domain/graphql/updateUserBrand";
import { GraphQlAdapter } from "@src/infrastructure/adapters/graphQl/GraphQl.adapter";
import { AdminRestApiPaths } from "@src/infrastructure/adapters/http";
import { MultipartFormService } from "@src/infrastructure/services/multipartform";

interface UpdateUserBrandGraphQLResponse {
  updateUserBrand: UserDetailsBrand;
}

function mapUpdateUserBrandResponse(
  response: UserDetailsBrand,
): UserDetailsBrand {
  return {
    brandId: response.brandId,
    brandName: response.brandName,
    brandLogoUrl: response.brandLogoUrl,
    organizationId: response.organizationId,
    organizationName: response.organizationName,
    brandUserMappingId: response.brandUserMappingId,
    isAccessActive: response.isAccessActive,
    roles: response.roles ?? [],
    features: response.features ?? [],
  };
}

export class UserRepository implements IUserRepository {
  private readonly client: ApolloClient;

  constructor(client: ApolloClient = GraphQlAdapter.getInstance().getClient()) {
    this.client = client;
  }

  async updateUser(params: UpdateUserDTO): Promise<IUpdateUserProps> {
    const { data } = await new MultipartFormService().post<{
      data: IUpdateUserProps;
    }>(AdminRestApiPaths.user, params.toMultipartFormData());

    if (!data?.userId || !data.authId?.trim()) {
      throw new Error("User save failed");
    }

    return data;
  }

  async updateUserBrand(params: UpdateUserBrandDTO): Promise<UserDetailsBrand> {
    const variables: Record<string, unknown> = {
      userId: params.userId,
      brandId: params.brandId,
      roleId: params.roleId,
      isActive: params.isActive,
    };
    if (params.customPermissions != null) {
      variables.customPermissions = params.customPermissions;
    }

    const result = await this.client.mutate<UpdateUserBrandGraphQLResponse>({
      mutation: UPDATE_USER_BRAND,
      variables,
      fetchPolicy: "no-cache",
      errorPolicy: "all",
    });

    const payload = result.data?.updateUserBrand;
    if (!payload) {
      const graphQlMessage =
        result.error instanceof Error ? result.error.message : null;
      throw new Error(
        graphQlMessage?.trim() || "Failed to assign brand to user",
      );
    }

    return mapUpdateUserBrandResponse(payload);
  }
}
